import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import {
    Recurrence,
    ControlsPageFiltersForControlsYearlyPageSpecificFilters as ControlsYearlyFilters,
    ControlsYearlyPageSpecificFilters,
    ControlsYearlyColumn,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";

import { decodeOrFilter, encodeOrFilter } from "../common";

import {
    createDecodePageFiltersFn,
    createEncodeColumnFn,
    createEncodePageFiltersFn,
} from "./common";

type AllColumns = { [K in ControlsYearlyColumn]: null };
const allColumnsGuard: AllColumns = {
    name: null,
    description: null,
    program: null,
    recurrenceYearly: null,
    notification: null,
    status: null,
    assignment: null,
    loginProtection: null,
    segregationOfDuties: null,
    tags: null,
    accessRight: null,
    financialYear: null,
    controlResultCount: null,
    nextDueDate: null,
};
const ALL_COLUMNS: ControlsYearlyColumn[] = Object.keys(
    allColumnsGuard
) as ControlsYearlyColumn[];
const encodeControlsYearlyColumn: (col: string) => ControlsYearlyColumn =
    createEncodeColumnFn(ALL_COLUMNS);

// Filters
//
function encodeControlsYearlySpecificPageFilters(
    filters: Array<SimpleOrFilter>
): ControlsYearlyPageSpecificFilters {
    return {
        notification: encodeOrFilter<number>("notification", filters),
        financialYear: encodeOrFilter<number>("financialYear", filters),
        recurrenceYearly: encodeOrFilter<Recurrence>(
            "recurrenceYearly",
            filters
        ),
    };
}
const encodeControlsYearlyPageFilters: (
    simpleAndFilter: SimpleAndFilter | null
) => ControlsYearlyFilters = createEncodePageFiltersFn(
    encodeControlsYearlySpecificPageFilters
);

function decodeControlsYearlyPageSpecificFilters(
    specificFilters: ControlsYearlyPageSpecificFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (specificFilters.notification) {
        output.push(
            decodeOrFilter<number>("notification", specificFilters.notification)
        );
    }
    if (specificFilters.financialYear) {
        output.push(
            decodeOrFilter<number>(
                "financialYear",
                specificFilters.financialYear
            )
        );
    }
    if (specificFilters.recurrenceYearly) {
        output.push(
            decodeOrFilter<Recurrence>(
                "recurrenceYearly",
                specificFilters.recurrenceYearly
            )
        );
    }
    return output;
}
const decodeControlsYearlyPageFilters: (
    pageFilters: ControlsYearlyFilters
) => Array<SimpleOrFilter> = createDecodePageFiltersFn(
    decodeControlsYearlyPageSpecificFilters
);

// Page state
//
type ControlsYearlyPageStateWithType = PageStateData<"ControlsYearly"> & {
    type: "ControlsYearly";
};

function decodeControlsYearlyPageState(
    pageState: ControlsYearlyPageStateWithType
): PageFilterAndColumns {
    const yearlyPageState = unwrapPageStateAs(pageState, "ControlsYearly");
    const filter: SimpleAndFilter = {
        filters: decodeControlsYearlyPageFilters(yearlyPageState.filters),
    };
    const visibleColumnIds = new Set(yearlyPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeControlsYearlyPageState(
    filterAndColumns: PageFilterAndColumns
): ControlsYearlyPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    return {
        type: "ControlsYearly",
        filters: encodeControlsYearlyPageFilters(filter),
        columns: Array.from(visibleColumnIds).map(encodeControlsYearlyColumn),
        version: filterAndColumns.version,
    };
}

export { encodeControlsYearlyPageState, decodeControlsYearlyPageState };

export type { ControlsYearlyPageStateWithType };
